import { debounce } from 'lodash';
import { useCallback, useRef } from 'react';

import { EWebTreatmentRequestLog, TreatmentRequestLog } from '@/entities/diagnosis/libs';
import useLogger from '@/hooks/useLogger';
import { SsnInputStatus, TreatmentRequest } from '@/models/diagnosis';
import { AccordionState } from './useTelemedicineRequestViewModel';

interface UseTelemedicineSsnParams {
  treatmentRequest: TreatmentRequest;
  updateForm: (updater: (prev: any) => any) => void;
  accordionRefs: Record<
    AccordionState,
    React.RefObject<{
      openSection: (options?: { scroll?: boolean }) => void;
      closeSection: () => void;
      toggleSection: () => void;
    }>
  >;
  hasVideoRequestCallType: boolean;
  detailSymptomInputRef: React.RefObject<HTMLTextAreaElement>;
}

export const useTelemedicineSsn = ({
  treatmentRequest,
  updateForm,
  accordionRefs,
  hasVideoRequestCallType,
  detailSymptomInputRef,
}: UseTelemedicineSsnParams) => {
  const logger = useLogger();
  const ssnInputRef = useRef<HTMLInputElement>(null);

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const focusDetailSymptomInput = useCallback(() => {
    return new Promise<void>((resolve) => {
      logger.log({
        eventName: EWebTreatmentRequestLog.ClickRequestTypeOpen,
      });
      requestAnimationFrame(() => {
        detailSymptomInputRef.current?.focus();
        resolve();
      });
    });
  }, [logger]);

  const nextFlowAfterSsn = async () => {
    blurActiveElement();

    if (hasVideoRequestCallType) {
      accordionRefs[AccordionState.Ssn].current?.closeSection();
      accordionRefs[AccordionState.RequestCallType].current?.openSection({ scroll: false });
      return;
    }

    accordionRefs[AccordionState.Ssn].current?.closeSection();
    accordionRefs[AccordionState.DetailSymptom].current?.openSection({ scroll: false });
    await focusDetailSymptomInput();
  };

  const updatePatientSsn = debounce((patientSsn: string) => {
    const form = treatmentRequest.updatePatientSsn(patientSsn);
    const ssnInputStatus = treatmentRequest.getCurrentSsnStatus();

    updateForm((prev) => ({ ...prev, ...form }));

    if (ssnInputStatus !== SsnInputStatus.Completed) {
      return;
    }

    logger.log({
      eventName: TreatmentRequestLog.RequestSuccessSsn,
    });

    nextFlowAfterSsn();
  }, 150);

  const resetPatientSsn = () => {
    const form = treatmentRequest.resetPatientSsn();
    updateForm((prev) => ({ ...prev, ...form }));
  };

  const focusSsnInput = () => {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        ssnInputRef.current?.focus();
        resolve();
      });
    });
  };

  return {
    ssnInputRef,
    updatePatientSsn,
    resetPatientSsn,
    focusSsnInput,
  };
};
